import React from 'react';
import { SparklesIcon } from '@heroicons/react/20/solid';
import { FaceSmileIcon } from '@heroicons/react/24/outline';

interface FacePresenceProps {
  swapCount: number;
}

const FacePresence: React.FC<FacePresenceProps> = ({ swapCount }) => {
  // Calculate presence level based on swap count
  const getPresenceLevel = (count: number) => {
    if (count >= 50) return {
      label: 'High Presence',
      description: 'Your content is making waves!',
      color: 'text-green-600',
      bg: 'bg-green-50',
      icon: 'bg-green-500',
      border: 'border-green-200'
    };
    if (count >= 20) return {
      label: 'Growing Presence',
      description: 'Building momentum nicely',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      icon: 'bg-blue-500',
      border: 'border-blue-200'
    };
    if (count >= 5) return {
      label: 'Emerging Presence',
      description: 'Getting started, keep going!',
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      icon: 'bg-indigo-500',
      border: 'border-indigo-200'
    };
    return {
      label: 'Limited Presence',
      description: 'Start creating to boost your presence',
      color: 'text-slate-600',
      bg: 'bg-slate-50',
      icon: 'bg-slate-500',
      border: 'border-slate-200'
    };
  };

  const presence = getPresenceLevel(swapCount);

  return (
    <div className={`relative overflow-hidden rounded-xl border ${presence.border} ${presence.bg} transition-colors duration-200`}>
      <div className="px-4 py-6 sm:px-6">
        <div className="flex items-center">
          <div className={`${presence.icon} rounded-lg p-3`}>
            <FaceSmileIcon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-sm font-medium text-slate-900">
              Face Presence
            </h3>
            <div className="mt-1 flex items-center gap-2">
              <span className={`text-2xl font-bold ${presence.color}`}>
                {swapCount}
              </span>
              <span className="text-sm text-slate-600">
                anonymizations this month
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${presence.bg} ${presence.color}`}>
              {presence.label}
            </span>
            {swapCount >= 50 && (
              <SparklesIcon className="h-4 w-4 text-amber-500" />
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {presence.description}
          </p>
        </div>

        <div className="mt-4 h-1.5 w-full rounded-full bg-slate-200">
          <div
            className={`h-full rounded-full ${presence.icon} transition-all duration-500`}
            style={{
              width: `${Math.min((swapCount / 50) * 100, 100)}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FacePresence;